/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable class-methods-use-this */
import { ReportGrammarRules } from '../../../../../../../redux/config/customReport/types'
import { CustomReportCategoryType } from '../../../../../../../redux/context/customReports/typesCategory'
import { BuilderNode, NodesById, NormalizedBuilderNode, State, VisitorContext } from '../types'

/**
 * Abstrakti visitor-luokka normalisoidun puurakenteen läpikäyntiin.
 * Tämä korvaa aiemman AbstractBuilderNodeVisitor-luokan.
 */
export abstract class NormalizedAbstractVisitor<T> {
  protected rules: ReportGrammarRules

  protected traverseAll: boolean

  protected nodesById: NodesById

  /**
   * @param rules Puun rakennetta ohjaavat säännöt
   * @param nodesById Normalisoitu puudata
   * @param traverseAll Traversoidaanko myös leaf-solmut
   */
  constructor(rules: ReportGrammarRules, nodesById: NodesById, traverseAll = false) {
    this.rules = rules
    this.nodesById = nodesById
    this.traverseAll = traverseAll
  }

  /**
   * Apumetodit sääntöjen tarkistukseen
   */
  protected isReferenceable(node: NormalizedBuilderNode, context: VisitorContext): boolean {
    return this.rules.referenceable.includes(node.type)
  }

  protected isTerminal(nodeType: CustomReportCategoryType): boolean {
    return this.rules.terminals.includes(nodeType)
  }

  protected isLeaf(nodeId: string, context: VisitorContext): boolean {
    const node = this.nodesById[nodeId]
    if (!node) return false

    if (context.isFunctionDecendant) {
      return false
    }

    if (node.type === 'function' || node.type === 'periodGroup') {
      return true
    }

    return false
  }

  /**
   * Aloittaa puun läpikäynnin annetusta solmusta
   */
  public visit(rootNodeId: string, initialContext: VisitorContext = { state: State.Root }): T {
    return this.visitNodeById(rootNodeId, initialContext)
  }

  /**
   * Vierailee solmussa ID:n perusteella - tärkeä metodi normalisoidussa rakenteessa
   */
  protected visitNodeById(nodeId: string, context: VisitorContext): T {
    const node = this.nodesById[nodeId]
    if (!node) {
      throw new Error(`Node with id ${nodeId} not found`)
    }

    // Määritellään konteksti solmutyypin perusteella
    const nodeContext = this.createContext(node, context)

    // Kutsutaan tyypin mukaista visit-metodia
    const result = this.dispatchVisitByType(node, nodeContext)

    // Traverse children unless we're stopping at Leaf nodes and this is a Leaf
    if (this.traverseAll || !this.isLeaf(nodeId, nodeContext)) {
      for (const childId of node.childIds || []) {
        // Jos tämä solmu on funktio, lisätään lapsille tieto siitä, että ne ovat funktion jälkeläisiä
        const childContext = node.type === 'function' ? { ...nodeContext, isFunctionDecendant: true } : nodeContext

        this.visitNodeById(childId, childContext)
      }
    }

    return result
  }

  // NormalizedAbstractVisitor-luokan createContext-metodi päivitettynä
  protected createContext(node: NormalizedBuilderNode, parentContext: VisitorContext): VisitorContext {
    switch (node.type) {
      case 'root':
        return { ...parentContext, state: State.Root }
      case 'company':
        return { ...parentContext, company: node.value as string, state: State.Company }
      case 'dimension':
        return { ...parentContext, dimension: node.value as string, state: State.Dimension }
      case 'periodGroup':
        return { ...parentContext, state: State.PeriodGroup }
      case 'function':
        // Tärkeä muutos: Tässä EI ASETETA isFunctionDecendant:true funktiolle itselleen
        // vaan säilytetään vanhemman konteksti sellaisenaan
        return { ...parentContext }
      default:
        return parentContext
    }
  }

  /**
   * Ohjaa kutsun tyypin mukaiselle visit-metodille
   */
  protected dispatchVisitByType(node: NormalizedBuilderNode, context: VisitorContext): T {
    switch (node.type) {
      case 'root':
        return this.visitRootNode(node, context)
      case 'company':
        return this.visitCompanyNode(node, context)
      case 'dimension':
        return this.visitDimensionNode(node, context)
      case 'periodGroup':
        return this.visitPeriodGroupNode(node, context)
      case 'function':
        return this.visitFunctionNode(node, context)
      case 'reference':
        return this.visitReferenceNode(node, context)
      case 'keyFigureId':
        return this.visitKeyFigureNode(node, context)
      case 'formulaId':
        return this.visitFormulaNode(node, context)
      default:
        throw new Error(`Unknown node type: ${node.type}`)
    }
  }

  /**
   * Tyyppispesifit vierailumetodit, jotka perivät luokat toteuttavat
   */
  abstract visitRootNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitCompanyNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitDimensionNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitPeriodGroupNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitFunctionNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitReferenceNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitKeyFigureNode(node: NormalizedBuilderNode, context: VisitorContext): T

  abstract visitFormulaNode(node: NormalizedBuilderNode, context: VisitorContext): T

  /**
   * Apumetodi, joka rakentaa hierarkkisen puun normalisoidusta datasta (tarvittaessa)
   */
  protected buildTree(nodeId: string): BuilderNode {
    const node = this.nodesById[nodeId]
    if (!node) {
      throw new Error(`Node with id ${nodeId} not found`)
    }

    return {
      ...node,
      children: (node.childIds || []).map(childId => this.buildTree(childId))
    }
  }

  /**
   * Apumetodi lapsisolmujen hakemiseen
   */
  protected getChildIds(nodeId: string): string[] {
    const node = this.nodesById[nodeId]
    return node?.childIds || []
  }

  /**
   * Hakee kaikki jälkeläissolmut (mukaan lukien annettu solmu)
   */
  protected getAllDescendantIds(nodeId: string): string[] {
    const node = this.nodesById[nodeId]
    if (!node) return []

    return [nodeId, ...(node.childIds || []).flatMap(childId => this.getAllDescendantIds(childId))]
  }
}
